import {
	Participant,
	getBroadcastGroups,
	getDivisions,
	getMe,
	getOperatingUnits,
	getRoles,
	getWorkplaces,
} from 'core/API/broadcast';
import { Role } from 'core/API/roles';
import { GetParticipantsList, GetUsersList } from 'core/API/users';
import { Division } from 'core/interfaces/userInterface';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { IPaginatedResponse, UserMe } from 'types';
import useUserDataStore from 'store/useUserDataStore';

export const useWorkspaceResources = (incomingWorkplace?: number) => {
	const { user } = JSON.parse(localStorage.getItem('BYOD-app'));
	return useQuery(
		['users', incomingWorkplace || user.id],
		() => GetUsersList(incomingWorkplace || user.id),
		{
			staleTime: Infinity,
			refetchIntervalInBackground: false,
			refetchOnMount: false,
		}
	);
};

export const useRoles = () => {
	return useQuery<IPaginatedResponse<Role>>('broadcast/roles', getRoles, {
		staleTime: Infinity,
		refetchIntervalInBackground: false,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	});
};

export const useDivision = () => {
	return useQuery<IPaginatedResponse<Division>>('broadcast/divisions', getDivisions, {
		staleTime: Infinity,
		refetchIntervalInBackground: false,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	});
};

export const useWorkplaces = (searchByOperatingUnits?: number[]) => {
	return useQuery<IPaginatedResponse<any>>(
		['broadcast/workplaces', searchByOperatingUnits],
		() => getWorkplaces(searchByOperatingUnits),
		{
			staleTime: Infinity,
			refetchIntervalInBackground: false,
			refetchOnMount: false,
			refetchOnWindowFocus: false,
		}
	);
};

export const useBroadcastGroups = (workplaceId: number, limit: number = 200) => {
	const [intervalTime, setIntervalTime] = useState(5000); // Start at 5 seconds

	const query = useQuery(
		['broadcast-groups', workplaceId, limit],
		() => getBroadcastGroups(workplaceId, limit),
		{
			select: (data) => {
				return data.items;
			},
			staleTime: Infinity,
			refetchOnMount: false,
			refetchOnWindowFocus: false,
		}
	);

	useEffect(() => {
		const interval = setInterval(() => {
			if (query.data?.length > 0) {
				query.refetch();
				setIntervalTime(5000); // Reset interval to 5 seconds if there is new data
			} else if (intervalTime < 60000) {
				setIntervalTime((prev) => Math.min(prev + 5000, 60000)); // Increment by 5 seconds, max 1 minute
			}
		}, intervalTime);

		return () => clearInterval(interval);
	}, [query, intervalTime]);

	return query;
};

export const useParticipants = () => {
	const { workplaceId } = useWorkplacesContext();

	return useQuery<Participant[]>(
		['broadcast/participants', workplaceId],
		() => GetParticipantsList(workplaceId),
		{
			staleTime: Infinity,
			enabled: !!workplaceId,
			refetchIntervalInBackground: false,
			refetchOnMount: false,
			refetchOnWindowFocus: false,
		}
	);
};

export const useOperatingUnits = (searchByDivision?: number[]) => {
	return useQuery<IPaginatedResponse<any>>(
		['broadcast/operating-units', searchByDivision],
		() => getOperatingUnits(searchByDivision),
		{
			staleTime: Infinity,
			refetchIntervalInBackground: false,
			refetchOnMount: false,
			refetchOnWindowFocus: false,
		}
	);
};
// Custom hook to use the /me endpoint
export const useMe = () => {
	const { setCurrentUserRole } = useUserDataStore();

	return useQuery<UserMe>('me', getMe, {
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		onSuccess: (data) => {
			console.log("updating user on /me call", data);
			// Update localStorage with the latest user data
			const storedData = localStorage.getItem('BYOD-app');
			if (storedData) {
				try {
					const parsedData = JSON.parse(storedData);
					parsedData.user = data;
					localStorage.setItem('BYOD-app', JSON.stringify(parsedData));
				} catch (error) {
					console.error('Failed to update user data in localStorage:', error);
				}
			}

			// Keep current user role up to date whenever /me is called
			if (data?.securityRole) {
				// Map SecurityRole to SecurityRoleResponse shape
				const securityRoleResponse = {
					...data.securityRole,
					id: +(data.securityRole['@id'].split('/').pop() ?? 0),
					'@id': data.securityRole['@id'] ?? '',
					createdAt: data.securityRole.createdAt ?? '',
					createdBy: data.securityRole.createdBy ?? '',
					deleted: data.securityRole.deleted ?? false,
					identifier: data.securityRole.identifier ?? '',
					name: data.securityRole.name ?? '',
					securityRolePermission: data.securityRole.securityRolePermission ?? [],
					updatedAt: data.securityRole.updatedAt ?? '',
					updatedBy: data.securityRole.updatedBy ?? '',
					workplace: data.securityRole.workplace ?? '',
					securityRoleType: data.securityRole.securityRoleType,
				};
				setCurrentUserRole(securityRoleResponse);
			}
		},
	});
};
